import {
  Activity,
  BadgeCheck,
  BarChart3,
  BookOpen,
  Cpu,
  Globe2,
  Layers,
  Lock,
  Network,
  Rocket,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Zap,
} from "@/components/icons";
import { Images } from "@/assests";
import { STATS } from "@/lib/constants";
import type {
  ColorGroup,
  IconSample,
  LogoSymbol,
  LogoVariant,
  PrincipleItem,
  TocItem,
  TypeSpec,
} from "./types";

export const TOC: TocItem[] = [
  { id: "overview", label: "Overview" },
  { id: "logo", label: "Logo system" },
  { id: "logo-usage", label: "Logo usage" },
  { id: "color", label: "Color palette" },
  { id: "typography", label: "Typography" },
  { id: "iconography", label: "Iconography" },
  { id: "motion", label: "Motion & feel" },
  { id: "voice", label: "Voice & tone" },
];

export const LOGO_VARIANTS: LogoVariant[] = [
  {
    id: "wordmark-dark",
    label: "Primary — on dark",
    description: "Default lockup. Use on Proxidize black (#0A0A0B) or any near-black surface.",
    src: Images.logoDark,
    bg: "#0A0A0B",
    builder: { asset: "wordmark", color: "white" },
  },
  {
    id: "wordmark-light",
    label: "Inverse — on light",
    description: "Inverted lockup. Use on white, off-white, or pale brand surfaces.",
    src: Images.logoOnLight,
    bg: "#F7F7F8",
    builder: { asset: "wordmark", color: "black" },
  },
];

export const LOGO_SYMBOLS: LogoSymbol[] = [
  {
    label: "Symbol — on dark",
    description:
      "The “X” mark with the brand underscore on Proxidize black. Use as the app icon, favicon, or avatar. Keep the underscore — it carries the mark.",
    src: "/brand-kit/proxidize-icon-black.svg",
    builder: { asset: "symbol", color: "white", background: "black" },
  },
  {
    label: "Symbol — on light",
    description:
      "The inverse mark on white. Use on light, off-white, or pale brand surfaces. Never recolor the glyph outside the approved palette.",
    src: "/brand-kit/proxidize-icon-white.svg",
    builder: { asset: "symbol", color: "black", background: "white" },
  },
];

export const LOGO_DOS = [
  "Maintain the underscore detail — it carries the brand mark.",
  "Preserve minimum clearspace equal to the width of the “o” glyph on every side.",
  "Place the wordmark over solid surfaces or low-noise gradients only.",
  "Use the inverse lockup on light backgrounds — never the dark mark on white.",
];

export const LOGO_DONTS = [
  "Don't rotate, skew, outline or apply drop-shadows to the wordmark.",
  "Don't recolor the wordmark outside of the approved palette.",
  "Don't stretch the lockup or alter the spacing between glyphs.",
  "Don't place the logo over busy imagery without a tint layer at 60 %+ opacity.",
];

export const COLOR_GROUPS: ColorGroup[] = [
  {
    id: "core",
    label: "Core surfaces",
    caption: "The dark-only foundation the entire product lives on.",
    swatches: [
      {
        name: "Background",
        token: "--background",
        hex: "#0A0A0B",
        description: "Page canvas",
        foreground: "light",
      },
      {
        name: "Surface 1",
        token: "--surface-1",
        hex: "#111113",
        description: "Cards · panels",
        foreground: "light",
      },
      {
        name: "Surface 2",
        token: "--surface-2",
        hex: "#161618",
        description: "Inputs · muted",
        foreground: "light",
      },
      {
        name: "Surface 3",
        token: "--surface-3",
        hex: "#1C1C1E",
        description: "Elevated · modals",
        foreground: "light",
      },
    ],
  },
  {
    id: "brand",
    label: "Brand primitives",
    caption: "The two signals that make Proxidize feel like Proxidize.",
    swatches: [
      {
        name: "Brand white",
        token: "--brand",
        hex: "#F7F7F8",
        description: "Primary foreground",
        foreground: "dark",
      },
      {
        name: "Brand yellow",
        token: "--brand-yellow",
        hex: "#22d3ee",
        description: "Accent · highlight",
        foreground: "dark",
      },
      {
        name: "Brand green",
        token: "--brand-green",
        hex: "#5b8cff",
        description: "Live · network · ok",
        foreground: "dark",
      },
    ],
  },
  {
    id: "support",
    label: "Support & system",
    caption: "Borders, muted copy, and signals shared across the system.",
    swatches: [
      {
        name: "Border",
        token: "--border",
        hex: "#27272A",
        description: "Hairline · divider",
        foreground: "light",
      },
      {
        name: "Muted foreground",
        token: "--muted-foreground",
        hex: "#8B8B8B",
        description: "Secondary copy",
        foreground: "light",
      },
    ],
  },
];

export const TYPE_SPECS: TypeSpec[] = [
  {
    role: "Display",
    family: "Geist Sans",
    weight: "700 · Bold",
    size: "48 / 64 px",
    tracking: "−0.02em",
    sample: "Proxy infrastructure, refined.",
    className: "text-4xl sm:text-5xl font-bold tracking-tight",
  },
  {
    role: "Headline",
    family: "Geist Sans",
    weight: "600 · Semibold",
    size: "28 / 36 px",
    tracking: "−0.01em",
    sample: "Built for developers, enterprises, and AI teams.",
    className: "text-2xl sm:text-3xl font-semibold tracking-tight",
  },
  {
    role: "Body",
    family: "Geist Sans",
    weight: "400 · Regular",
    size: "16 px / 1.6",
    tracking: "0",
    sample: `${STATS.ips} real IPs across ${STATS.countries} countries with ${STATS.uptime} uptime.`,
    className: "text-base leading-relaxed",
  },
  {
    role: "Mono",
    family: "Geist Mono",
    weight: "500 · Medium",
    size: "13 px",
    tracking: "0",
    sample: "curl -x http://proxidize.com:8080 https://api.example.com",
    className: "text-[13px] font-mono",
  },
];

export const ICON_SAMPLES: IconSample[] = [
  { name: "Network", Icon: Network },
  { name: "Globe2", Icon: Globe2 },
  { name: "ShieldCheck", Icon: ShieldCheck },
  { name: "Cpu", Icon: Cpu },
  { name: "Smartphone", Icon: Smartphone },
  { name: "BarChart3", Icon: BarChart3 },
  { name: "Activity", Icon: Activity },
  { name: "Layers", Icon: Layers },
  { name: "Lock", Icon: Lock },
  { name: "Rocket", Icon: Rocket },
  { name: "Sparkles", Icon: Sparkles },
  { name: "Zap", Icon: Zap },
  { name: "BadgeCheck", Icon: BadgeCheck },
  { name: "BookOpen", Icon: BookOpen },
  { name: "Shield", Icon: Shield },
];

export const MOTION_PRINCIPLES: PrincipleItem[] = [
  {
    title: "Calm by default",
    body: "Reveal on scroll with a 14px lift and 0.45s ease. Nothing bounces, nothing slides across the page.",
  },
  {
    title: "Honor reduced motion",
    body: "Every animation guards on useReducedMotion(). If the user opts out, layouts settle in their final state.",
  },
  {
    title: "Yellow + green carry energy",
    body: "Conic glow on the perimeter of CTAs and proxy inputs — the only place the palette is allowed to pulse.",
  },
  {
    title: "Easing curve",
    body: "cubic-bezier(0.22, 1, 0.36, 1) — fast start, decelerating finish. It matches how the product feels.",
  },
];

export const VOICE_PILLARS: PrincipleItem[] = [
  {
    title: "Direct, not dry",
    body: "Lead with the value. State capability before context. We earn trust through specificity, not adjectives.",
  },
  {
    title: "Technical, in plain English",
    body: "Engineers are the audience. Use the right term, but never hide behind it. If a sentence needs a glossary, rewrite it.",
  },
  {
    title: "Confident, never loud",
    body: "We don't shout. We don't oversell. The infrastructure does the talking — the copy just gets out of the way.",
  },
  {
    title: "Human, not corporate",
    body: "Contractions are fine. First-person plural is fine. Marketing-speak (synergy, leverage, unlock) is not.",
  },
];

export const OVERVIEW_STATS = [
  { label: "Brand version", value: "v1.0" },
  { label: "System", value: "Dark-only" },
  { label: "Grid", value: "8 pt" },
  { label: "Last updated", value: "2026" },
];
